import React, { useState, useEffect } from 'react';
import { verifyBulkAndFilterSafe, saveVerifiedLeads, getVerifiedLeads } from '../services/emailVerifier.js';
import { FaCheck, FaTimes, FaSpinner, FaEye, FaDownload } from 'react-icons/fa';

const EmailVerificationPanel = ({ 
  emails = [], 
  rawData = [], 
  sessionId, 
  onVerificationComplete,
  onLeadsSelected,
  config
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState(null);
  const [verifiedLeads, setVerifiedLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [error, setError] = useState(null);
  const [apiKey, setApiKey] = useState(config?.reoonApiKey || '');
  const [showApiKeyInput, setShowApiKeyInput] = useState(true);

  console.log('🔍 EmailVerificationPanel: Component rendering');
  console.log('📧 Emails received:', emails?.length || 0);
  console.log('📊 Raw data received:', rawData?.length || 0);
  console.log('🆔 Session ID:', sessionId);
  console.log('⚙️ Config:', config);
  console.log('🔑 Config API Key:', config?.reoonApiKey);
  console.log('🔑 Current API Key:', apiKey);
  console.log('👁️ Show API Key Input:', showApiKeyInput);

  // Load existing verified leads on component mount
  useEffect(() => {
    if (sessionId) {
      loadExistingLeads();
    }
  }, [sessionId]);

  // Update API key when config changes
  useEffect(() => {
    if (config?.reoonApiKey) {
      setApiKey(config.reoonApiKey);
      // Keep showApiKeyInput as true so button is always visible
    }
  }, [config?.reoonApiKey]);

  const loadExistingLeads = async () => {
    try {
      const existingLeads = await getVerifiedLeads(sessionId);
      setVerifiedLeads(existingLeads);
    } catch (error) {
      console.error('Error loading existing leads:', error);
    }
  };

  const handleVerifyEmails = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your Reoon API key');
      return;
    }

    if (!emails || emails.length === 0) {
      setError('No emails to verify');
      return;
    }

    // Check for already verified emails
    const existingEmails = verifiedLeads.map(lead => lead.email);
    const newEmails = emails.filter(email => !existingEmails.includes(email));
    
    if (newEmails.length === 0) {
      setError('All emails have already been verified in this session');
      return;
    }

    if (newEmails.length < emails.length) {
      console.log(`Skipping ${emails.length - newEmails.length} already verified emails`);
    }

    setIsVerifying(true);
    setError(null);

    try {
      console.log('Starting verification for', newEmails.length, 'new emails (', emails.length - newEmails.length, 'already verified)');
      const results = await verifyBulkAndFilterSafe(newEmails, apiKey);
      
      console.log('Verification results:', results);
      setVerificationResults(results);

      // Combine email verification results with original CSV data
      const enrichedLeads = results.safe_emails.map(({ email, details }) => {
        const originalData = rawData.find(row => 
          row.email?.toLowerCase() === email.toLowerCase() ||
          row.Email?.toLowerCase() === email.toLowerCase()
        ) || {};

        return {
          email,
          verification_details: details,
          is_safe_to_send: true,
          company: originalData.company || originalData.Company || '',
          first_name: originalData.firstName || originalData['First Name'] || originalData.first_name || '',
          last_name: originalData.lastName || originalData['Last Name'] || originalData.last_name || '',
          job_title: originalData.jobTitle || originalData['Job Title'] || originalData.job_title || '',
          url: originalData.website || originalData.Website || originalData.url || '',
          phone_number: originalData.phoneNumber || originalData['Phone Number'] || originalData.phone_number || '',
          country: originalData.country || originalData.Country || '',
          industry: originalData.industry || originalData.Industry || '',
          company_linkedin: originalData.companyLinkedIn || originalData['Company LinkedIn'] || originalData.company_linkedin || '',
          person_linkedin: originalData.personLinkedIn || originalData['Person LinkedIn'] || originalData.person_linkedin || '',
          ...originalData // Include any other fields from CSV
        };
      });

      // Save to Supabase
      if (enrichedLeads.length > 0) {
        const saveResult = await saveVerifiedLeads(enrichedLeads, sessionId);
        console.log('Saved leads to database:', saveResult);
        
        // Reload leads from database to get IDs
        await loadExistingLeads();
      }

      // Note: Verification results are now stored in Supabase only
      // No need to store in localStorage to avoid duplicates

      if (onVerificationComplete) {
        onVerificationComplete(results);
      }

      setShowApiKeyInput(false);
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.message || 'Failed to verify emails');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleLeadSelection = (leadId, isSelected) => {
    setSelectedLeads(prev => {
      if (isSelected) {
        return [...prev, leadId];
      } else {
        return prev.filter(id => id !== leadId);
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === verifiedLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(verifiedLeads.map(lead => lead.id));
    }
  };

  const handleAnalyzeSelected = () => {
    if (selectedLeads.length === 0) {
      setError('Please select at least one lead to analyze');
      return;
    }

    const selectedLeadData = verifiedLeads.filter(lead => selectedLeads.includes(lead.id));
    
    // Format data to match CSV upload structure for dashboard analysis
    const formattedLeads = selectedLeadData.map(lead => ({
      email: lead.email,
      company: lead.company || '',
      firstName: lead.firstName || lead.first_name || '',
      lastName: lead.lastName || lead.last_name || '',
      jobTitle: lead.jobTitle || lead.job_title || lead['Job Title'] || '',
      companyLinkedIn: lead.companyLinkedIn || lead.company_linkedin || lead['Company LinkedIn'] || '',
      linkedinUrl: lead.linkedin_url || lead.person_linkedin || lead['Person LinkedIn'] || '', // Use linkedin_url from database
      linkedinUsername: lead.linkedin_username || '', // Use linkedin_username from database
      country: lead.country || lead.Country || '',
      industry: lead.industry || lead.Industry || '',
      phoneNumber: lead.phoneNumber || lead.phone_number || lead['Phone Number'] || '',
      url: lead.url || lead.website || '', // Use 'url' property to match websiteAnalyzer expectations
      rawData: lead // Keep original data for reference
    }));
    
    if (onLeadsSelected) {
      onLeadsSelected(formattedLeads);
    }
  };

  const exportSelectedLeads = () => {
    const selectedLeadData = verifiedLeads.filter(lead => selectedLeads.includes(lead.id));
    
    const csvContent = [
      ['Email', 'Company', 'First Name', 'Last Name', 'Job Title', 'Website', 'Phone Number', 'Country', 'Industry', 'Company LinkedIn', 'LinkedIn URL', 'LinkedIn Username', 'Verification Status'],
      ...selectedLeadData.map(lead => [
        lead.email,
        lead.company || '',
        lead.firstName || lead.first_name || '',
        lead.lastName || lead.last_name || '',
        lead.jobTitle || lead.job_title || lead['Job Title'] || '',
        lead.url || lead.website || '',
        lead.phoneNumber || lead.phone_number || lead['Phone Number'] || '',
        lead.country || lead.Country || '',
        lead.industry || lead.Industry || '',
        lead.companyLinkedIn || lead.company_linkedin || lead['Company LinkedIn'] || '',
        lead.linkedin_url || lead.person_linkedin || lead['Person LinkedIn'] || '',
        lead.linkedin_username || '',
        'Verified Safe'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verified_leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="email-verification-panel">

      <div className="verification-header">
        <h3>Email Verification</h3>
        {verifiedLeads.length > 0 ? (
          <p>
            {emails.length} emails loaded • {verifiedLeads.length} already verified • 
            {emails.filter(email => !verifiedLeads.map(lead => lead.email).includes(email)).length} new emails to verify
          </p>
        ) : (
          <p>Verify {emails.length} emails and filter safe-to-send addresses</p>
        )}
        <div className="session-info">
          <span className="session-label">Session:</span>
          <span className="session-id">{sessionId}</span>
        </div>
      </div>

      {showApiKeyInput && (
        <div className="api-key-section">
          <label htmlFor="apiKey">Reoon API Key:</label>
          <div className="api-key-input-group">
            <input
              type="password"
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your Reoon API key"
              className="api-key-input"
            />
            <button 
              onClick={handleVerifyEmails}
              disabled={isVerifying || !apiKey.trim()}
              className="verify-button"
            >
              {isVerifying ? (
                <>
                  <FaSpinner className="spinner" />
                  Verifying...
                </>
              ) : (
                <>
                  <FaCheck />
                  Verify Emails
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <FaTimes /> {error}
        </div>
      )}

      {verificationResults && (
        <div className="verification-stats">
          <div className="stat-card">
            <span className="stat-number">{verificationResults.stats.count_total}</span>
            <span className="stat-label">Total Emails</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{verificationResults.stats.safe_count}</span>
            <span className="stat-label">Safe to Send</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">
              {Math.round((verificationResults.stats.safe_count / verificationResults.stats.count_total) * 100)}%
            </span>
            <span className="stat-label">Success Rate</span>
          </div>
        </div>
      )}

      {verifiedLeads.length > 0 && (
        <div className="verified-leads-section">
          <div className="leads-header">
            <h4>Verified Safe Leads ({verifiedLeads.length})</h4>
            <div className="leads-actions">
              <button 
                onClick={handleSelectAll}
                className="select-all-button"
              >
                {selectedLeads.length === verifiedLeads.length ? 'Deselect All' : 'Select All'}
              </button>
              <button 
                onClick={exportSelectedLeads}
                disabled={selectedLeads.length === 0}
                className="export-button"
              >
                <FaDownload /> Export Selected
              </button>
              <button 
                onClick={handleAnalyzeSelected}
                disabled={selectedLeads.length === 0}
                className="analyze-button"
              >
                <FaEye /> Analyze Selected ({selectedLeads.length})
              </button>
            </div>
          </div>

          <div className="table-container">
            <div className="leads-table">
            <div className="table-header">
              <div className="header-cell checkbox-cell">Select</div>
              <div className="header-cell">Email</div>
              <div className="header-cell">Company</div>
              <div className="header-cell">First Name</div>
              <div className="header-cell">Last Name</div>
              <div className="header-cell">Job Title</div>
              <div className="header-cell">Website</div>
              <div className="header-cell">Phone</div>
              <div className="header-cell">Country</div>
              <div className="header-cell">Industry</div>
              <div className="header-cell">Status</div>
            </div>
            
            <div className="table-body">
              {verifiedLeads.map((lead) => (
                <div key={lead.id} className="table-row">
                  <div className="table-cell checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={(e) => handleLeadSelection(lead.id, e.target.checked)}
                    />
                  </div>
                  <div className="table-cell">{lead.email}</div>
                  <div className="table-cell">{lead.company}</div>
                  <div className="table-cell">{lead.firstName || lead.first_name || ''}</div>
                  <div className="table-cell">{lead.lastName || lead.last_name || ''}</div>
                  <div className="table-cell">{lead.jobTitle || lead.job_title || lead['Job Title'] || ''}</div>
                  <div className="table-cell">{lead.url || lead.website || ''}</div>
                  <div className="table-cell">{lead.phoneNumber || lead.phone_number || lead['Phone Number'] || ''}</div>
                  <div className="table-cell">{lead.country || lead.Country || ''}</div>
                  <div className="table-cell">{lead.industry || lead.Industry || ''}</div>
                  <div className="table-cell">
                    <span className="status-badge verified">
                      <FaCheck /> Verified
                    </span>
                    {!emails.includes(lead.email) && (
                      <span className="session-badge">
                        Previous Session
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .email-verification-panel {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          margin-top: 20px;
        }

        .verification-header {
          margin-bottom: 24px;
        }

        .verification-header h3 {
          margin: 0 0 8px 0;
          color: #1f2937;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .session-info {
          margin-top: 12px;
          padding: 8px 12px;
          background: #f8fafc;
          border-radius: 6px;
          border-left: 3px solid #3b82f6;
        }

        .session-label {
          font-weight: 500;
          color: #374151;
          margin-right: 8px;
        }

        .session-id {
          font-family: monospace;
          background: #e5e7eb;
          padding: 2px 6px;
          border-radius: 3px;
          color: #1f2937;
          font-size: 0.9rem;
        }

        .verification-header p {
          margin: 0;
          color: #6b7280;
          font-size: 0.95rem;
        }

        .api-key-section {
          margin-bottom: 24px;
        }

        .api-key-section label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #374151;
        }

        .api-key-input-group {
          display: flex;
          gap: 12px;
        }

        .api-key-input {
          flex: 1;
          padding: 12px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.95rem;
          transition: border-color 0.2s;
        }

        .api-key-input:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .verify-button {
          padding: 12px 24px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s;
        }

        .verify-button:hover:not(:disabled) {
          background: #2563eb;
        }

        .verify-button:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .error-message {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }

        .verification-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .stat-card {
          background: #f8fafc;
          padding: 16px;
          border-radius: 8px;
          text-align: center;
          border: 1px solid #e2e8f0;
        }

        .stat-number {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 0.85rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .verified-leads-section {
          margin-top: 24px;
        }

        .leads-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .leads-header h4 {
          margin: 0;
          color: #1f2937;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .leads-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .select-all-button,
        .export-button,
        .analyze-button {
          padding: 8px 16px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          color: #374151;
          cursor: pointer;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }

        .analyze-button {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }

        .analyze-button:hover:not(:disabled) {
          background: #059669;
        }

        .analyze-button:disabled {
          background: #9ca3af;
          border-color: #9ca3af;
          cursor: not-allowed;
        }

        .export-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .leads-table {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow-x: auto;
          background: white;
        }

        .table-container {
          overflow-x: auto;
          max-width: 100%;
        }

        .table-header {
          display: grid;
          grid-template-columns: 60px 200px 150px 120px 120px 150px 150px 120px 100px 120px 120px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          gap: 8px;
          align-items: center;
          min-width: 1400px;
        }

        .header-cell {
          padding: 12px;
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
        }

        .table-body {
          max-height: 400px;
          overflow-y: auto;
        }

        .table-row {
          display: grid;
          grid-template-columns: 60px 200px 150px 120px 120px 150px 150px 120px 100px 120px 120px;
          border-bottom: 1px solid #f3f4f6;
          gap: 8px;
          align-items: center;
          min-width: 1400px;
        }

        .table-row:hover {
          background: #f9fafb;
        }

        .table-cell {
          padding: 12px;
          font-size: 0.9rem;
          color: #374151;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .checkbox-cell {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .status-badge.verified {
          background: #d1fae5;
          color: #065f46;
        }

        .session-badge {
          display: inline-block;
          margin-left: 8px;
          padding: 2px 6px;
          background: #f3f4f6;
          color: #6b7280;
          border-radius: 3px;
          font-size: 0.7rem;
          font-weight: 400;
        }

        @media (max-width: 768px) {
          .api-key-input-group {
            flex-direction: column;
          }

          .leads-header {
            flex-direction: column;
            align-items: stretch;
          }

          .leads-actions {
            justify-content: stretch;
          }

          .leads-actions button {
            flex: 1;
            justify-content: center;
          }

          .table-header,
          .table-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .table-cell {
            padding: 8px 12px;
          }

          .header-cell {
            display: none;
          }

          .table-cell:before {
            content: attr(data-label);
            font-weight: 600;
            margin-right: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default EmailVerificationPanel;